type Freebuff = ((...args: unknown[]) => void) & { q?: unknown[][] };
type Configuration = { enabled?: boolean; testOnly?: boolean; conversion?: { eventId:string; clickId:string } | null };
let loaded = false;
let sdkReady = false;
let generation = 0;
let running: Promise<void> | null = null;
let trackingAllowed: (() => boolean) | null = null;
const landingEvents = ['freebuff_walkthrough_started', 'freebuff_target_completed', 'freebuff_prompt_copied', 'freebuff_example_played', 'freebuff_signin_requested', 'freebuff_run_requested'] as const;
export type FreebuffLandingEvent = typeof landingEvents[number];
const landingEventsSent = new Set<FreebuffLandingEvent>();
export const freebuffLoaded = () => loaded;
const optedOutCookie = () => /(?:^|;\s*)rill_analytics_enabled=0(?:;|$)/.test(document.cookie);
const gpc = () => Boolean((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl);
const clickCookie = () => document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith('bfcid='))?.slice(6);
export function stopFreebuff() {
  generation++;
  trackingAllowed = null;
  // Drop provider identifiers and queued retries before the shared lifecycle reloads the document.
  for (const name of ['bfcid', 'bfvid', 'bfvis']) {
    try {
      if (typeof document === 'undefined') continue;
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
      document.cookie = `${name}=; Domain=userill.dev; Path=/; Max-Age=0; SameSite=Lax`;
    } catch { /* Storage may be blocked. */ }
  }
  try { localStorage.removeItem('bfq'); localStorage.removeItem('bfql'); } catch { /* Storage may be blocked. */ }
}
/** Shared advertising lifecycle calls this before and after account preference resolution. */
export function startFreebuff(hasOptedOut: () => boolean): Promise<void> {
  if (running) return running;
  const currentGeneration = generation;
  const allowed = () => {
    try {
      return typeof window !== 'undefined' && typeof document !== 'undefined' && typeof navigator !== 'undefined'
        && generation === currentGeneration && !hasOptedOut() && !gpc() && !optedOutCookie();
    } catch { return false; } // Blocked browser storage fails closed.
  };
  running = (async () => {
    trackingAllowed = null;
    if (!allowed()) return;
    try {
      const response = await fetch('/api/advertising/freebuff', { credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(3000) });
      if (!response.ok || !allowed()) return;
      const config = await response.json() as Configuration;
      if (!config.enabled || !allowed()) return;
      // Non-production must never initialize the tag with a live click, including a stored one.
      let navigationClick: string | null = null;
      try {
        const entry = performance.getEntriesByType('navigation')[0];
        if (entry) navigationClick = new URL(entry.name).searchParams.get('bfcid');
      } catch { /* Navigation timing may be unavailable. */ }
      const initialClick = new URLSearchParams(location.search).get('bfcid') ?? navigationClick ?? clickCookie();
      if (config.testOnly && !initialClick?.startsWith('bfc_test_')) return;
      const target = window as Window & { freebuff?: Freebuff };
      if (!sdkReady) {
        target.freebuff = target.freebuff || Object.assign((...args:unknown[]) => { (target.freebuff!.q ||= []).push(args); }, { q:[] as unknown[][] });
        const script = document.createElement('script');
        script.async = true; script.src = 'https://freebuff.com/freebuff-tag.v2.js?t=fbt_67K6JnGLS5-sdBkbb82aew'; script.referrerPolicy = 'no-referrer';
        // The hosted tag reads PerformanceNavigationTiming.name if Router replaced the initial URL.
        // Loading is asynchronous and never gates React rendering or authentication.
        loaded = true; // Opt-out must unload even a script that is still downloading.
        const ready = await new Promise<boolean>(resolve => {
          const finish = (ok:boolean) => { clearTimeout(timer); resolve(ok); };
          script.onload = () => finish(true); script.onerror = () => finish(false);
          const timer = setTimeout(() => finish(false),8000);
          document.head.appendChild(script);
        });
        sdkReady = ready;
        if (!ready || !allowed()) return;
      }
      trackingAllowed = allowed;
      const event = config.conversion;
      if (!event || event.clickId !== clickCookie() || !allowed()) return;
      const key = `rill_ad_freebuff_${event.eventId}`;
      try { if (sessionStorage.getItem(key)) return; } catch { /* Storage may be unavailable. */ }
      target.freebuff?.('conversion','signup_completed',{ eventId:event.eventId });
      try { sessionStorage.setItem(key,'1'); } catch { /* Replays retain the same idempotency key. */ }
    } catch { /* Provider availability never blocks the app. */ }
  })().finally(() => { running = null; });
  return running;
}

/** Public landing milestones only: no form values, walkthrough text or customer identifiers. */
export function trackFreebuffLanding(event: FreebuffLandingEvent): void {
  if (typeof window === 'undefined' || !/^\/freebuff\/?$/.test(window.location.pathname)
    || !landingEvents.includes(event) || landingEventsSent.has(event)) return;
  landingEventsSent.add(event);
  const currentGeneration = generation;
  // Wait for the initial consent/configuration check if it is still in flight. Never retry a failed event.
  void (running ?? Promise.resolve()).then(() => {
    if (generation === currentGeneration && sdkReady && trackingAllowed?.()) {
      (window as Window & { freebuff?: Freebuff }).freebuff?.('track', event, { page: '/freebuff' });
    }
  }).catch(() => {});
}
